import create from '@ep/components/create/element';
import { getDataFields } from '@ep/components/create/autoform';
import ajax from '@ep/tools/ajax';
import toast from '@ep/tools/toast';
import { candidateActionStates, isCrmOperationalAvailable, leadDossierActionStates } from './candidateCrmCapability.js';

// "Ver dossiê" abre o Report genérico do EP (`candidates.dossier.report`, key auxiliar do módulo) na superfície documental.
// O dataset vem do contrato key + context filtrado pelo candidato; o tenant e a autorização continuam sendo os da sessão.
const CANDIDATES_CONTEXT = Object.freeze({ scope: 'product', product: 'admindigital', service: 'crm', domain: 'prospect', module: 'candidates' });

const openDossierReport = async ({ record = {} } = {}) => {
  const candidateId = record.candidate_id || record.candidateId;
  if (!candidateId) return;
  return create({
    element: 'report',
    key: 'dossier',
    surface: 'viewer',
    context: { ...CANDIDATES_CONTEXT },
    conditions: [{ key: 'candidate_id', operator: '=', value: candidateId }],
  });
};

const d = (path, fallback = '') => {
  const parts = String(path || '').split('.').filter(Boolean);
  let cursor = globalThis?.dic;
  for (const part of parts) {
    if (!cursor || typeof cursor !== 'object') return fallback;
    cursor = cursor[part];
  }
  return typeof cursor === 'string' ? cursor : fallback;
};

const sendCandidateToCrm = async ({ record = {}, component = null } = {}) => {
  const candidateTenantId = record.candidate_tenant_id || record.candidateTenantId;
  if (!candidateTenantId) return null;
  try {
    const response = await ajax.post({
      product: 'admindigital', category: 'crm', domain: 'prospect', module: 'leads',
      method: 'handoffFromCandidate', body: { candidate_tenant_id: candidateTenantId }, config: { loader: true },
    });
    if (response?.status !== 'success') throw Object.assign(new Error(d('candidates.errors.handoffFailed')), { response });
    toast({ type: 'success', message: d('candidates.messages.handoffCompleted') });
    if (component && typeof component.render === 'function') await component.render();
    return response;
  } catch (error) {
    toast({ type: 'error', message: error?.message || d('candidates.errors.handoffFailed') });
    return null;
  }
};

// "Atualizar Dossiê": ação manual com custo controlado no backend (prazo de 180 dias, execução única, cache, cota/gasto). Abrir/imprimir/baixar PDF nunca passa por aqui.
const refreshCandidateDossier = async ({ record = {}, component = null } = {}) => {
  const candidateTenantId = record.candidate_tenant_id || record.candidateTenantId;
  if (!candidateTenantId) return null;
  try {
    const response = await ajax.post({ product: 'admindigital', category: 'crm', domain: 'prospect', module: 'candidates', method: 'refreshDossier', body: { candidate_tenant_id: candidateTenantId }, config: { loader: true } });
    if (response?.status !== 'success') throw Object.assign(new Error(d('candidates.errors.dossierRefreshFailed')), { response });
    toast({ type: 'success', message: d('candidates.messages.dossierRefreshed') });
    if (component && typeof component.render === 'function') await component.render();
    return response;
  } catch (error) {
    const body = error?.response?.data || error?.response || {};
    const code = body?.code;
    const next = body?.next_eligible_at ? new Date(body.next_eligible_at).toLocaleDateString('pt-BR') : '';
    const message = code === 'DOSSIER_REFRESH_NOT_YET_ELIGIBLE' ? d('candidates.errors.dossierRefreshNotYet').replace('[[date]]', next)
      : code === 'DOSSIER_REFRESH_IN_PROGRESS' ? d('candidates.errors.dossierRefreshInProgress') : (error?.message || d('candidates.errors.dossierRefreshFailed'));
    toast({ type: 'error', message });
    return null;
  }
};

// Redação contextual REAL: o primeiro diálogo só seleciona uma Offer (nenhum custo).
// A IA só é invocada após envio explícito; o backend revalida identidade, qualificação,
// contato LinkedIn e a reserva financeira. Não existe template substituindo variáveis.
const generateCandidateConversation = async ({ record = {} } = {}) => {
  const candidateTenantId = record.candidate_tenant_id || record.candidateTenantId;
  if (!candidateTenantId) return null;
  const labels = globalThis?.dic?.candidates?.prospectingConversation || {};
  try {
    const found = await ajax.post({
      product: 'admindigital', category: 'crm', domain: 'prospect', module: 'candidates',
      method: 'getProspectingConversationChoices', body: { candidate_tenant_id: candidateTenantId },
      config: { loader: true },
    });
    if (found?.status !== 'success') throw new Error(d('candidates.prospectingConversation.generateFailed'));
    const choices = found.choices || [];
    if (!choices.length) {
      toast({ type: 'info', message: d('candidates.prospectingConversation.noChoices') });
      return null;
    }

    // getDataFields do EP apenas serializa o formulário e chama callback; nunca executa save.
    // O callback do AutoForm em modo new/edit só roda DEPOIS do save genérico.
    const selector = {
      element: 'autoform', parent: 'main', mode: 'new', key: 'main',
      scope: 'product', product: 'admindigital', service: 'crm', category: 'crm', domain: 'prospect', module: 'candidates',
      id: `CANDIDATE_AI_OUTREACH_SELECT_${Date.now()}`, isDialog: true, autoOpen: true, kill: true,
      dialog: { variant: 'modal' }, title: labels.chooseTitle, returnMode: 'object',
      groups: [{ legend: false, row: 0, col: 0, isGrid: true, content: [{
        name: 'offer_name', element: 'inputGroup', type: 'radio', label: labels.chooseLabel, helper: labels.chooseHint,
        options: choices.map((item) => ({ text: `${item.offer_name} · ${item.contact_name}`, value: item.offer_name })),
        value: choices[0].offer_name, required: true, full: true, row: 0, col: 0,
      }] }],
      buttons: [
        { text: labels.cancel, click: (event) => event.target?.closest?.('.dialog')?.instance?.close() },
        { text: labels.generate, click: (event) => getDataFields(event, selector) },
      ],
      callback: async (values) => {
        const chosen = choices.find((item) => item.offer_name === values?.offer_name);
        if (!chosen) return;
        try {
          const response = await ajax.post({
            product: 'admindigital', category: 'crm', domain: 'prospect', module: 'candidates',
            method: 'generateProspectingConversation',
            body: { candidate_tenant_id: candidateTenantId, offer_name: chosen.offer_name },
            config: { loader: true },
          });
          if (response?.status !== 'success' || !response.conversation?.first_message)
            throw new Error(d('candidates.prospectingConversation.generateFailed'));
          const draft = response.conversation;
          // Texto editável para revisão/cópia MANUAL. Não oferece botão de salvar
          // nem callback de persistência: nenhuma mensagem é enviada ou gravada.
          return create({
            element: 'autoform', parent: 'main', mode: 'new', key: 'main',
            scope: 'product', product: 'admindigital', service: 'crm', category: 'crm', domain: 'prospect', module: 'candidates',
            id: `CANDIDATE_AI_OUTREACH_DRAFT_${Date.now()}`,
            isDialog: true, autoOpen: true, kill: true, dialog: { variant: 'modal' },
            title: labels.generatedTitle, groups: [{
              legend: false, row: 0, col: 0, isGrid: true, content: [
                { name: 'angle', element: 'textarea', label: labels.angle, value: draft.angle, rows: 2, full: true },
                { name: 'invitation', element: 'textarea', label: labels.invitation, value: draft.invitation, rows: 3, full: true },
                { name: 'first_message', element: 'textarea', label: labels.firstMessage, value: draft.first_message, rows: 5, full: true },
                { name: 'follow_up', element: 'textarea', label: labels.followUp, value: draft.follow_up, rows: 3, full: true },
                { name: 'discovery_question', element: 'textarea', label: labels.discoveryQuestion, value: draft.discovery_question, rows: 2, full: true },
                { name: 'source_url', element: 'url', label: labels.source, value: draft.evidence?.source_url || '', full: true },
              ],
            }],
            buttons: [
              { text: labels.close, click: (event) => event.target?.closest?.('.dialog')?.instance?.close() },
            ],
          });
        } catch {
          toast({ type: 'error', message: d('candidates.prospectingConversation.generateFailed') });
        }
      },
    };
    return create(selector);
  } catch {
    toast({ type: 'error', message: d('candidates.prospectingConversation.generateFailed') });
    return null;
  }
};
const actionContext = (context = {}) => ({
  menuModules: context?.menuModules || context?.context?.menuModules || [],
  moduleRef: context?.moduleRef || context?.context?.moduleRef,
});

// Prospecção automática do tenant: o estado vive no owner (candidates) e o backend valida sessão e tenant; a UI lê e envia o booleano desejado.
const readAutomaticProspecting = async () => {
  const response = await ajax.post({ product: 'admindigital', category: 'crm', domain: 'prospect', module: 'candidates', method: 'getAutomaticProspecting', body: {} });
  if (response?.status !== 'success') throw new Error(d('candidates.errors.automaticProspectingFailed'));
  return response.enabled !== false;
};

const automaticProspectingState = (enabled) => ({
  checked: enabled,
  tooltip: enabled ? d('candidates.automatic.pauseTooltip') : d('candidates.automatic.resumeTooltip'),
});

const setAutomaticProspecting = async ({ checked } = {}) => {
  if (typeof checked !== 'boolean') return null;
  try {
    const response = await ajax.post({ product: 'admindigital', category: 'crm', domain: 'prospect', module: 'candidates', method: 'setAutomaticProspecting', body: { enabled: checked }, config: { loader: true } });
    if (response?.status !== 'success') throw new Error(d('candidates.errors.automaticProspectingFailed'));
    return response;
  } catch (error) {
    toast({ type: 'error', message: error?.message || d('candidates.errors.automaticProspectingFailed') });
    return null;
  }
};

// Contexto do CRM (Leads): mesmo Report e mesma operação do owner; as regras de visibilidade vêm de `leadDossierActionStates`.

export const candidateDossierActionRegistry = Object.freeze({
  'leads.dossier': { visible: (context = {}) => leadDossierActionStates(context).dossier.visible, enabled: (context = {}) => leadDossierActionStates(context).dossier.enabled, run: openDossierReport },
  'leads.refreshDossier': { visible: (context = {}) => leadDossierActionStates(context).refreshDossier.visible, enabled: (context = {}) => leadDossierActionStates(context).refreshDossier.enabled, run: refreshCandidateDossier },
  'candidates.generateProspectingConversation': {
    run: generateCandidateConversation,
  },
  'candidates.automaticProspecting': {
    resolve: async () => automaticProspectingState(await readAutomaticProspecting()),
    run: setAutomaticProspecting,
  },
  // "Ver dossiê": consulta nos estados finais (visibleWhen do config); independe do CRM e não substitui o envio.
  'candidates.dossier': {
    visible: (context = {}) => candidateActionStates(context, actionContext(context)).dossier.visible,
    enabled: (context = {}) => candidateActionStates(context, actionContext(context)).dossier.enabled,
    run: openDossierReport,
  },
  // "Atualizar Dossiê": só sem pacote CRM e só quando a janela de 180 dias está aberta (o backend revalida prazo, tenant e execução única).
  'candidates.refreshDossier': {
    visible: (context = {}) => candidateActionStates(context, actionContext(context)).refreshDossier.visible,
    enabled: (context = {}) => candidateActionStates(context, actionContext(context)).refreshDossier.enabled,
    run: refreshCandidateDossier,
  },
  // "Enviar para o CRM" (módulo Leads autorizado): habilitado só em `prospect`. A operação pertence ao owner Lead.
  'candidates.sendToCrm': {
    visible: (context = {}) => candidateActionStates(context, actionContext(context)).sendToCrm.visible,
    enabled: (context = {}) => candidateActionStates(context, actionContext(context)).sendToCrm.enabled,
    run: sendCandidateToCrm,
  },
});

export { isCrmOperationalAvailable };
export default candidateDossierActionRegistry;
